// Fetch wrapper for the Aleph backend, ported from the live desk's betaApi.ts.
//
// Rules it enforces (api-surface 1.2):
// - same-origin cookies, JSON in and out, 204 means "no body";
// - mutations send X-CSRF-Token read from the aleph_csrf cookie (or the
//   aleph_access_csrf cookie for access-challenge routes);
// - a 401 outside /api/beta/auth/ dispatches the window event
//   "aleph:session-expired" so the shell can drop to the sign-in door;
// - the access check gets a 10 s abort and three retries at 1, 4 and 10 s,
//   longer when the server sends Retry-After.

import {
  ACCESS_CHECK_RETRY_DELAYS_MS,
  ACCESS_CHECK_TIMEOUT_MS,
  API_PATHS,
  COOKIE_NAMES,
  HEADER_NAMES,
  RETRY_AFTER_MAX_MS,
  SESSION_EXPIRED_EVENT,
} from './config';
import { ApiError, CLIENT_ERROR_CODES, isApiError, isRetryableError, parseRetryAfter, toApiError } from './errors';

export type MutationScope = 'session' | 'access';

export interface ApiRequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly mutation?: MutationScope;
  readonly signal?: AbortSignal;
  readonly timeoutMs?: number;
  // Statuses returned as data instead of thrown (health/ready answers 503 with a body).
  readonly tolerateStatuses?: readonly number[];
}

export interface ApiResponse<T> {
  readonly status: number;
  readonly body: T;
}

interface ErrorBody {
  readonly error?: unknown;
  readonly message?: unknown;
}

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const entry = document.cookie.split('; ').find((part) => part.startsWith(prefix));
  return entry ? decodeURIComponent(entry.slice(prefix.length)) : null;
}

function eventTarget(): EventTarget | null {
  return typeof window === 'undefined' ? null : window;
}

export function emitSessionExpired(): void {
  eventTarget()?.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

export function subscribeSessionExpired(onExpired: () => void): () => void {
  const target = eventTarget();
  if (!target) return () => undefined;
  target.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
  return () => target.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
}

function csrfHeader(scope: MutationScope): string {
  const cookieName = scope === 'access' ? COOKIE_NAMES.accessCsrf : COOKIE_NAMES.csrf;
  const token = readCookie(cookieName);
  if (!token) {
    throw new ApiError(403, CLIENT_ERROR_CODES.csrfUnavailable, 'The browser security token is unavailable. Sign in again.');
  }
  return token;
}

// Links the caller's signal (TanStack cancellation) with our own timeout.
function linkedSignal(external: AbortSignal | undefined, timeoutMs: number | undefined): { signal: AbortSignal; dispose: () => void } {
  const controller = new AbortController();
  const onExternalAbort = () => controller.abort(external?.reason);
  if (external) {
    if (external.aborted) controller.abort(external.reason);
    else external.addEventListener('abort', onExternalAbort, { once: true });
  }
  const timer = timeoutMs === undefined
    ? null
    : setTimeout(() => controller.abort(new DOMException('Request timed out.', 'AbortError')), timeoutMs);
  return {
    signal: controller.signal,
    dispose: () => {
      if (timer !== null) clearTimeout(timer);
      external?.removeEventListener('abort', onExternalAbort);
    },
  };
}

function textOf(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const raw = await response.text();
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    throw new ApiError(response.status, CLIENT_ERROR_CODES.invalidJson, `The server answered ${response.status} without JSON.`);
  }
}

export async function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
  const headers = new Headers({ Accept: 'application/json' });
  const hasBody = options.body !== undefined;
  if (hasBody) headers.set('Content-Type', 'application/json');
  if (options.mutation) headers.set(HEADER_NAMES.csrf, csrfHeader(options.mutation));

  const link = linkedSignal(options.signal, options.timeoutMs);
  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? (hasBody ? 'POST' : 'GET'),
      headers,
      credentials: 'same-origin',
      signal: link.signal,
      ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted) {
      throw new ApiError(0, CLIENT_ERROR_CODES.cancelled, 'The request was cancelled by the caller.');
    }
    throw toApiError(error, 'The request failed before the server answered.');
  } finally {
    link.dispose();
  }

  const body = await parseBody(response);
  if (response.ok || options.tolerateStatuses?.includes(response.status)) {
    return { status: response.status, body: body as T };
  }
  if (response.status === 401 && !path.startsWith(API_PATHS.authPrefix)) {
    emitSessionExpired();
  }
  const errorBody = (body ?? {}) as ErrorBody;
  throw new ApiError(
    response.status,
    textOf(errorBody.error) ?? CLIENT_ERROR_CODES.requestFailed,
    textOf(errorBody.message) ?? `Request returned ${response.status}.`,
    parseRetryAfter(response.headers.get('Retry-After')),
  );
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const response = await apiFetch<T>(path, options);
  return response.body;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// How long to wait before the next attempt: the schedule, or the server's own
// Retry-After when it asks for longer, capped at RETRY_AFTER_MAX_MS.
export function retryWaitMs(error: unknown, scheduledMs: number): number {
  const asked = isApiError(error) ? error.retryAfterMs : null;
  if (asked === null) return scheduledMs;
  return Math.min(Math.max(scheduledMs, asked), RETRY_AFTER_MAX_MS);
}

// Runs `attempt` once, then again after each delay while the failure is
// retryable. Non-retryable failures (401, 403, CSRF missing) surface
// immediately; 429 and 5xx are retried, because on this backend both are
// load artefacts that answer 200 seconds later.
export async function withRetry<T>(attempt: () => Promise<T>, delays: readonly number[] = ACCESS_CHECK_RETRY_DELAYS_MS): Promise<T> {
  for (let index = 0; ; index += 1) {
    try {
      return await attempt();
    } catch (error) {
      const delay = delays[index];
      if (delay === undefined || !isRetryableError(error)) throw error;
      await sleep(retryWaitMs(error, delay));
    }
  }
}

// The workspace access check: 10 s abort per attempt, [1, 4, 10] s retries.
export function accessRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  return withRetry(() => apiRequest<T>(path, { ...options, timeoutMs: options.timeoutMs ?? ACCESS_CHECK_TIMEOUT_MS }));
}

export function buildQuery(params: Readonly<Record<string, string | number | boolean | null | undefined>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}
